import { Button, Dropdown } from '@ibsys/kit';
import { PlusIcon } from 'lucide-react';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EDITABLE_PHRASE_TYPES, type EditablePhraseType } from '@/entities/conversations';
import { cn } from '@/shared/lib';

type Props = {
  /** Кнопка видна всегда, а не по наведению на промежуток: в пустой ленте промежутков нет */
  isAlwaysVisible: boolean;
  disabled: boolean;
  onAdd: (type: EditablePhraseType) => void;
};

/**
 * Промежуток между соседними записями ленты (над первой и под последней тоже) с добавлением записи в него:
 * сначала выбирается её вид.
 */
export const TranscriptAddRecordGap: FC<Props> = ({ isAlwaysVisible, disabled, onAdd }) => {
  const { t } = useTranslation(['dialogs']);

  const options = useMemo(
    () =>
      EDITABLE_PHRASE_TYPES.map((type) => ({
        type: 'item' as const,
        key: type,
        title: t(`dialogs:transcriptEdit.recordType.${type}`),
        onClick: () => onAdd(type),
      })),
    [t, onAdd],
  );

  return (
    <div className={cn(isAlwaysVisible ? 'flex justify-center py-1' : 'group relative h-3')}>
      <Dropdown options={options}>
        <Button
          size="sm"
          variant="outline"
          icon={<PlusIcon />}
          disabled={disabled}
          // Скрытая кнопка остаётся в порядке обхода с клавиатуры и показывается при фокусе; указатель
          // она не перехватывает, пока скрыта, — иначе её край накрывал бы соседние строки.
          // Прозрачность неактивной кнопки из kit перебивала бы скрытие, поэтому у неактивной она задана явно
          className={cn(
            !isAlwaysVisible &&
              'absolute top-1/2 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2 pointer-events-none opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:pointer-events-auto data-[state=open]:opacity-100 disabled:opacity-0! group-hover:disabled:opacity-50!',
          )}
        >
          {t('dialogs:transcriptEdit.addRecord')}
        </Button>
      </Dropdown>
    </div>
  );
};
